import React from 'react';
import { motion } from 'framer-motion';
import neochatLogo from '../assets/images/neochat-logo.png';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const Terms = () => {
  const { isAuthenticated } = useAuth();
  return (
    <div className="min-h-screen bg-white container">
      {/* Navigation */}
      <nav className="fixed w-full z-50 bg-white/80 backdrop-blur-md border-b border-gray-100">
        <div className="mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <Link to="/"><img src={neochatLogo} alt="Neochat Logo" className="h-35px w-auto" /></Link>
            </div>
            <div className="flex items-center space-x-6">
              <Link to="/about" className="text-gray-600 hover:text-gray-900">About Us</Link>
              <Link to="/contact" className="text-gray-600 hover:text-gray-900 ml-2">Contact</Link>
              {isAuthenticated ? (
                <Link
                  to="/dashboard"
                  className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors"
                >
                  Go to Dashboard
                </Link>
              ) : (
                <>
                  <Link
                    to="/login"
                    className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors"
                  >
                    Login
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-12 bg-gradient-to-r from-blue-600 to-indigo-700">
        <div className="mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <motion.img
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              src={neochatLogo}
              alt="Neochat Logo"
              className="h-16 mx-auto mb-8"
            />
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-4xl font-bold text-white mb-4"
            >
              Terms & Conditions
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="text-xl text-blue-100"
            >
              Last updated: {new Date().toLocaleDateString()}
            </motion.p>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">1. Agreement to Terms</h2>
            <p className="text-gray-600 mb-6">
              By accessing or using Neochat AI Solutions' services, you agree to be bound by these Terms and Conditions. If you disagree with any part of these terms, you may not access our services.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mb-6">2. Use License</h2>
            <p className="text-gray-600 mb-6">
              Permission is granted to temporarily access our services for personal or business use, subject to the following restrictions:
            </p>
            <ul className="list-disc pl-6 text-gray-600 mb-6">
              <li>You must not modify or copy the materials</li>
              <li>You must not use the materials for any commercial purpose</li>
              <li>You must not attempt to reverse engineer any software</li>
              <li>You must not remove any copyright or proprietary notations</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 mb-6">3. Service Description</h2>
            <p className="text-gray-600 mb-6">
              Neochat AI Solutions provides AI-powered chatbot services. We reserve the right to modify or discontinue any service at any time without notice.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mb-6">4. User Responsibilities</h2>
            <p className="text-gray-600 mb-6">
              As a user of our services, you agree to:
            </p>
            <ul className="list-disc pl-6 text-gray-600 mb-6">
              <li>Provide accurate and complete information</li>
              <li>Maintain the security of your account</li>
              <li>Use the services in compliance with all applicable laws</li>
              <li>Not engage in any activity that disrupts or interferes with our services</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 mb-6">5. Payment Terms</h2>
            <p className="text-gray-600 mb-6">
              All payments are processed securely through our payment partners. You agree to pay all fees associated with your use of our services.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mb-6">6. Intellectual Property</h2>
            <p className="text-gray-600 mb-6">
              All content, features, and functionality of our services are owned by Neochat AI Solutions and are protected by international copyright, trademark, and other intellectual property laws.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mb-6">7. Limitation of Liability</h2>
            <p className="text-gray-600 mb-6">
              Neochat AI Solutions shall not be liable for any indirect, incidental, special, consequential, or punitive damages resulting from your use of or inability to use our services.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mb-6">8. Changes to Terms</h2>
            <p className="text-gray-600 mb-6">
              We reserve the right to modify these terms at any time. We will notify users of any material changes by posting the new terms on our website.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mb-6">9. Contact Information</h2>
            <p className="text-gray-600">
              For any questions regarding these Terms & Conditions, please contact us at:
            </p>
            <p className="text-gray-600">
              Email: legal@neochat.com<br />
              Address: [Your Business Address]
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <h3 className="text-lg font-semibold mb-4">Neochat</h3>
              <p className="text-gray-400">AI-powered chatbot solutions for modern businesses.</p>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">Quick Links</h3>
              <ul className="space-y-2">
                <li><Link to="/about" className="text-gray-400 hover:text-white">About Us</Link></li>
                <li><Link to="/contact" className="text-gray-400 hover:text-white">Contact</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">Legal</h3>
              <ul className="space-y-2">
                <li><Link to="/privacy" className="text-gray-400 hover:text-white">Privacy Policy</Link></li>
                <li><Link to="/terms" className="text-gray-400 hover:text-white">Terms & Conditions</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">Contact</h3>
              <ul className="space-y-2">
                <li className="text-gray-400">support@neochat.io</li>
                <li className="text-gray-400">+6281-1821-8365</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>© {new Date().getFullYear()} Neochat. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Terms; 